// Couverture du portefeuille — logique pure.
//
// Un croisement de deux facettes (par exemple filière × classe de bioagresseur) :
// chaque case compte les projets qui portent les deux valeurs. Une case vide est
// une combinaison qu'aucun projet ne couvre ; elle ne dit pas qu'elle devrait
// l'être, faute de référentiel des combinaisons attendues.

import { comparerFr } from "./portefeuille";
import type { IdFacette, Index } from "./facettes";

export interface EnteteCroisement {
  cle: string;
  libelle: string;
  /** Projets qui portent la valeur. */
  total: number;
}

export interface Croisement {
  lignes: EnteteCroisement[];
  colonnes: EnteteCroisement[];
  /** Indices des projets de chaque case, `cases[ligne][colonne]`. */
  cases: number[][][];
}

function entetes(index: Index, facette: IdFacette): EnteteCroisement[] {
  const parCle = new Map<string, EnteteCroisement>();
  for (const ligne of index) {
    for (const [cle, libelle] of ligne[facette]) {
      const e = parCle.get(cle) ?? { cle, libelle, total: 0 };
      e.total++;
      parCle.set(cle, e);
    }
  }
  return [...parCle.values()].sort((a, b) => b.total - a.total || comparerFr(a.libelle, b.libelle));
}

/** Croise `ligne` et `colonne` ; lignes et colonnes par effectif décroissant. */
export function croiser(index: Index, ligne: IdFacette, colonne: IdFacette): Croisement {
  const lignes = entetes(index, ligne);
  const colonnes = entetes(index, colonne);
  const cases = lignes.map((l) =>
    colonnes.map((c) =>
      index.flatMap((x, i) => (x[ligne].has(l.cle) && x[colonne].has(c.cle) ? [i] : [])),
    ),
  );
  return { lignes, colonnes, cases };
}
